import {
  CsvExportModule,
  CustomFilterModule,
  ModuleRegistry,
  RenderApiModule,
  RowApiModule,
  TextFilterModule,
  TooltipModule,
  ValidationModule,
} from 'ag-grid-community';
import {
  ColumnApiModule,
  ColumnMenuModule,
  ColumnsToolPanelModule,
  LicenseManager,
  ServerSideRowModelModule,
  SetFilterModule,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { getConnectorSettings } from 'Apis/library';

import { EmptyState } from 'Components/molecules/EmptyState';
import EmptyStateIcon from 'Assets/icons/engagement.svg';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { useI18n } from 'Hooks/useI18n';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import { ContentfulContext } from 'Rdx/contexts/contentfulContext';
import _ from 'lodash';
import { usersOrgFilterFromOrgModel } from 'Core-utils/helpers/orgModelFilters';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { useRefreshServerSideWhen } from '../AgGridBase';
import {
  AgGridToolingComplianceProps,
  COLUMN_STATE_STORAGE_KEYS,
  DEFAULT_COLUMN_STATE,
  DEFAULT_COL_DEF,
  GRID_CONFIG,
  findingsTheme,
} from './AgGridCompliance.config';
import usersInsightsColumns from './getUsersInsightsColumns';
import { ServerSideDataSource } from './ServerSideDataSource';
import {

  mapUrlParamsToGridState,
} from './toolingGridUrl';

ModuleRegistry.registerModules([
  ServerSideRowModelModule,
  TextFilterModule,
  CustomFilterModule,
  TooltipModule,
  SetFilterModule,
  ColumnApiModule,
  RenderApiModule,
  RowApiModule,
  ValidationModule,
  CsvExportModule,
  ColumnsToolPanelModule,
  ColumnMenuModule,
]);

const agGridLicenseKey = process.env.REACT_APP_AG_GRID_LICENSE_KEY;
if (agGridLicenseKey) {
  LicenseManager.setLicenseKey(agGridLicenseKey);
}

const ModalToolingComplianceGrid: React.FC<AgGridToolingComplianceProps> = ({
  defaultFilter,
  tableBodyHeight,
  emptyStateDescription = 'No tooling coverage records match your filters.',
}) => {
  const gridRef = useRef<any>(null);
  const columnStateLoadedRef = useRef(false);
  const filterModelAppliedRef = useRef(false);
  const initialGridStateRef = useRef(mapUrlParamsToGridState({}));
  const { filter: urlFilter, isUrlLoading } = useSearchParamsState();
  const urlFilterRef = useRef(urlFilter);
  urlFilterRef.current = urlFilter;

  const { contentfulData } = useContext(ContentfulContext);
  const providerNameIntegrationMap = contentfulData?.providerNameIntegrationMap;
  const installedConnectorMap = useSelector((state: any) => state.installedConnectorMap);
  const organization = useSelector((state: RootState) => state.orgModel.org);

  const { data: tenantSettings, isLoading: isSettingsLoading } = useApiObjectData(
    'CONNECTOR_SETTINGS',
    getConnectorSettings,
    (a) => a,
  );
  const { t } = useI18n();
  const connectorDataHook = useConnectorData();

  useEffect(() => {
    if (!isUrlLoading) {
      initialGridStateRef.current = mapUrlParamsToGridState(urlFilter);
    }
  }, [isUrlLoading, urlFilter]);

  const handleRowCountChange = useCallback((filtered: number, total: number) => {
    // Use effective rendered row count (includes synthetic "Unassigned" row path).
    if (total === 0) {
      gridRef.current?.api?.showNoRowsOverlay();
    } else {
      gridRef.current?.api?.hideOverlay();
    }
  }, []);

  // Default grid view excludes the synthetic Unassigned row.
  // Unassigned assets are shown only via explicit filters/insight tile.
  const defaultGridFilter = useMemo(
    () => ({
      assignmentFilter: 'assigned',
      ...usersOrgFilterFromOrgModel(organization),
      ...(defaultFilter ?? {}),
    }),
    [defaultFilter, organization],
  );

  const { datasource } = ServerSideDataSource({
    defaultFilter: defaultGridFilter,
    onRowCountChange: handleRowCountChange,
  });

  useRefreshServerSideWhen(gridRef, organization);

  const datasourceRef = useRef(datasource);
  datasourceRef.current = datasource;
  const stableDatasourceRef = useRef({
    getRows: (params: any) => datasourceRef.current.getRows(params),
  });
  const stableDatasource = stableDatasourceRef.current;

  const { isFetchingConnectors } = connectorDataHook;

  const gridContext = useMemo(() => ({
    tenantSettings,
    providerNameIntegrationMap,
    isLoading: isSettingsLoading || isFetchingConnectors,
    allConnectors: installedConnectorMap,
    connectorDataHook,
    t,
  }), [
    tenantSettings,
    providerNameIntegrationMap,
    isSettingsLoading,
    isFetchingConnectors,
    installedConnectorMap,
    connectorDataHook,
    t,
  ]);

  useEffect(() => {
    if (isUrlLoading || !filterModelAppliedRef.current) {
      return;
    }
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }
    const nextState = mapUrlParamsToGridState(urlFilter);
    const currentFilterModel = api.getFilterModel() ?? {};
    if (!_.isEqual(currentFilterModel, nextState.filterModel)) {
      api.setFilterModel(nextState.filterModel);
    }
    const currentSortModel = (api.getColumnState() ?? [])
      .filter((col: any) => typeof col.sort === 'string' && col.sort)
      .map((col: any) => ({ colId: col.colId, sort: col.sort }));
    if (!_.isEqual(currentSortModel, nextState.sortModel)) {
      if (nextState.sortModel.length > 0) {
        const targetSort = nextState.sortModel[0];
        const nextColumnState = (api.getColumnState() ?? []).map((col: any) =>
          col.colId === targetSort.colId ? { ...col, sort: targetSort.sort } : { ...col, sort: null },
        );
        api.applyColumnState({ state: nextColumnState, applyOrder: true });
      } else {
        const nextColumnState = (api.getColumnState() ?? []).map((col: any) => ({ ...col, sort: null }));
        api.applyColumnState({ state: nextColumnState, applyOrder: true });
      }
    }
  }, [isUrlLoading, urlFilter]);

  const onGridReady = useCallback((params: any) => {
    if (!(Array.isArray(usersInsightsColumns) && usersInsightsColumns.length > 0)) {
      return;
    }
    const savedState = localStorage.getItem(COLUMN_STATE_STORAGE_KEYS.server);
    if (savedState) {
      try {
        params.api.applyColumnState({ state: JSON.parse(savedState), applyOrder: true });
      } catch {
        params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
      }
    } else {
      params.api.applyColumnState({ state: DEFAULT_COLUMN_STATE, applyOrder: true });
    }
    columnStateLoadedRef.current = true;

    const state = initialGridStateRef.current;
    params.api.setFilterModel(state.filterModel);
    if (Array.isArray(state.sortModel) && state.sortModel.length > 0) {
      const sortCol = state.sortModel[0];
      const columnState = params.api.getColumnState();
      const newState = columnState.map((col: any) =>
        col.colId === sortCol.colId ? { ...col, sort: sortCol.sort } : { ...col, sort: null },
      );
      params.api.applyColumnState({ state: newState, applyOrder: true });
    }
    filterModelAppliedRef.current = true;
  }, []);

  const getRowHeight = (params: any) => {
    const baseHeight = GRID_CONFIG.rowHeight;
    const row = params.data;
    if (row && Array.isArray(row.assets) && row.assets.length > 1) {
      return baseHeight + (row.assets.length - 1) * 40;
    }
    return baseHeight;
  };

  return (
    <div className="d-flex flex-column ag-grid-theme--amp-dark" style={{ height: tableBodyHeight || GRID_CONFIG.defaultHeight, minHeight: GRID_CONFIG.minHeight }}>
      <AgGridReact
          ref={gridRef}
          theme={findingsTheme}
          columnDefs={usersInsightsColumns}
          context={gridContext}
          rowModelType="serverSide"
          serverSideDatasource={stableDatasource}
          getRowId={(params) => String(params.data?.__rowId ?? params.data?.id ?? '')}
          domLayout="normal"
          suppressAggFuncInHeader
          defaultColDef={DEFAULT_COL_DEF}
          getRowHeight={getRowHeight}
          headerHeight={GRID_CONFIG.headerHeight}
          cacheBlockSize={GRID_CONFIG.cacheBlockSize}
          onGridReady={onGridReady}
          sideBar={false}
          noRowsOverlayComponent={EmptyState}
          noRowsOverlayComponentParams={{
            ImageSrc: EmptyStateIcon,
            noDescription: false,
            description: emptyStateDescription,
          }}
        />
    </div>
  );
};

export default ModalToolingComplianceGrid;
